/**
 * Diagnostic-trace validation and payload construction.
 *
 * Lifted out of SupportTicketCreate so the Yup schema can reach the same rules
 * the payload builder uses. Two copies of seven constraints is how the message
 * an operator reads drifts from the payload the server receives.
 */
/**
 * Nothing in the trace section has been touched.
 *
 * Both the validator and the payload builder need this exact question, and
 * they must answer it identically: the builder's guard is what stands between
 * an untouched section and `new Date('').toISOString()`, which throws
 * RangeError: Invalid time value. Two copies of the condition is one edit away
 * from a crash on submit, so there is one.
 */
function isUntouchedTrace(
  traceId: string,
  from: string,
  to: string,
  servicesText: string
): boolean {
  return !traceId.trim() && !from.trim() && !to.trim() && !servicesText.trim()
}

/**
 * What a service name may look like.
 *
 * Exported so the entry control can reject a malformed name as it is typed
 * rather than on submit. That control must use THIS constant and not a second
 * spelling of it: a stricter copy would refuse names the server accepts, and a
 * looser one would let a name through to fail later, which is the behaviour
 * the control exists to avoid.
 *
 * Anchored and deliberately not global — `String.match` with a /g pattern
 * ignores anchors and reports matches anywhere in the string, which would
 * admit "not a service name" on the strength of the substring "not".
 */
export const SERVICE_NAME_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/

/**
 * The services named in a trace, as a list.
 *
 * The draft carries them as one comma-separated string, and this was written
 * out twice — once in the validator and once in the payload builder — which is
 * how the count a message reports drifts from the count that is sent. It is
 * lossless in both directions because SERVICE_NAME_PATTERN admits no commas.
 */
export function splitServices(servicesText: string): string[] {
  return Array.from(
    new Set(
      servicesText
        .split(',')
        .map((service) => service.trim())
        .filter(Boolean)
    )
  )
}

/**
 * The two halves of a trace boundary, and how to put one back together.
 *
 * The draft carries each boundary as one `YYYY-MM-DDTHH:mm` string, which is
 * what a native datetime-local input produced and what `new Date()` parses in
 * the validator and the payload builder. Those are the tested copy, so the
 * controls adapt to that string rather than the string changing shape to suit
 * the controls — the same reason traceServices stays CSV.
 *
 * A boundary with only one half filled is left malformed ON PURPOSE. It is a
 * half-answered question, and the validator already has the message for it
 * ("Enter a valid start and end time"); silently completing it with today's
 * date or midnight would submit a window nobody chose.
 */
export function traceDatePart(boundary: string): string {
  return boundary.split('T')[0] || ''
}

export function traceTimePart(boundary: string): string {
  return boundary.split('T')[1] || ''
}

/**
 * Recombine, collapsing to the empty string when NEITHER half is set.
 *
 * That collapse is load-bearing rather than tidy: `isUntouchedTrace` asks
 * whether the section is blank, and a bare "T" left by a cleared field is not
 * blank. It would make an untouched section look touched, and the reporter
 * would be told to fix a window they never opened.
 */
export function joinTraceBoundary(date: string, time: string): string {
  if (!date.trim() && !time.trim()) return ''
  return `${date}T${time}`
}

/**
 * Which constraint a diagnostic trace fails, as a translation key.
 *
 * These seven rules previously lived inside one boolean whose only output was
 * `undefined`, which disabled Create and said nothing. An admin who mistyped a
 * single character of a 32-character trace id got a dead button and no
 * diagnosis — and the one-hour window limit was documented only in the helper
 * text of the SERVICES field, not on either date field it governs.
 *
 * Returns undefined when the trace is usable (or switched off).
 */
export function diagnosticTraceProblem(
  enabled: boolean,
  traceId: string,
  from: string,
  to: string,
  servicesText: string
): string | undefined {
  if (!enabled) return undefined

  // An EMPTY trace is not a broken trace — it is a user who ticked the box and
  // has nothing to put in it.
  //
  // A W3C trace id is an engineering artefact: it lives in HTTP headers and
  // server logs and is displayed nowhere in this panel, so a reporter has no
  // route to obtain one. Treating blank as invalid meant ticking a checkbox
  // disabled Create with "Trace ID must be 32 hexadecimal characters", and the
  // only escape was to notice that unticking the box released it. The form
  // asked the customer to do the debugging.
  //
  // So: validate what is TYPED, never demand that something be typed. A blank
  // trace section submits as no diagnostic context at all, which is the same
  // outcome as leaving the box unticked and is what the reporter meant.
  if (isUntouchedTrace(traceId, from, to, servicesText)) return undefined

  const services = splitServices(servicesText)
  const fromDate = new Date(from)
  const toDate = new Date(to)
  const duration = toDate.getTime() - fromDate.getTime()

  if (!/^[0-9a-f]{32}$/.test(traceId.trim())) {
    return 'Trace ID must be 32 hexadecimal characters.'
  }
  if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
    return 'Enter a valid start and end time.'
  }
  if (duration <= 0) {
    return 'The end time must be after the start time.'
  }
  if (duration > 60 * 60 * 1000) {
    return 'The window cannot be longer than one hour.'
  }
  if (services.length === 0) {
    return 'Name at least one service.'
  }
  if (services.length > 20) {
    return 'Name at most twenty services.'
  }
  if (services.some((service) => !SERVICE_NAME_PATTERN.test(service))) {
    return 'Service names use lowercase letters, digits, hyphens and underscores.'
  }

  return undefined
}

export function buildDiagnosticTraceInput(
  enabled: boolean,
  traceId: string,
  from: string,
  to: string,
  servicesText: string
) {
  if (!enabled) return undefined

  // Load-bearing, and NOT redundant with the validator's guard.
  //
  // An untouched section is a NON-problem, so diagnosticTraceProblem returns
  // undefined for it and `if (problem) return` below never fires. Without this
  // line the builder runs on to `new Date('').toISOString()`, which throws
  // RangeError: Invalid time value and takes the submit handler with it.
  //
  // An earlier version of this comment said it would emit the string
  // "Invalid Date". It does not — that is what String(new Date('')) gives;
  // toISOString throws. The guard prevents a crash, not a bad payload.
  if (isUntouchedTrace(traceId, from, to, servicesText)) return undefined

  const services = splitServices(servicesText)
  const fromDate = new Date(from)
  const toDate = new Date(to)
  if (diagnosticTraceProblem(enabled, traceId, from, to, servicesText)) {
    return undefined
  }
  return {
    traceId: traceId.trim(),
    from: fromDate.toISOString(),
    to: toDate.toISOString(),
    services
  }
}
